<template>
  <Section
    :id="id"
    :aria-label="t('common.highlights', 2)"
    class="carrier-portal-highlight-section p-0"
  >
    <template #header>
      <h2>{{ primaryAppointment.label }}</h2>
      <div class="text-gray-darkest text-xl font-bold">
        {{ primaryAppointmentDate }}
      </div>
      <div class="text-gray-darker">
        {{
          shipment.status
            ? translateEnum(shipment.status, 'ShipmentStatus')
            : ''
        }}
      </div>
    </template>

    <ShipmentProgressIndicators :shipment="shipment" />

    <div class="flex border-top-1 border-color-divider">
      <div class="cell">
        <KeyValuePair :label="t('shipments.modes')" :value="mode" juicy />
      </div>
      <div class="cell">
        <KeyValuePair
          :label="t('shipments.services')"
          :value="shipment.service"
          juicy
        />
      </div>
      <div class="cell">
        <KeyValuePair
          :label="t('carriers.equipmentTypes')"
          :value="shipment.equipmentType?.name"
          juicy
        />
      </div>
      <div class="cell">
        <KeyValuePair
          :label="t('shipments.transitTime')"
          :value="
            acceptedRateQuote.transitTime !== undefined
              ? t('rateShop.countDays', {
                  count: acceptedRateQuote.transitTime,
                })
              : '-'
          "
          juicy
        />
      </div>
      <div class="cell">
        <KeyValuePair :label="mileage.label" :value="mileage.value" juicy />
      </div>
    </div>
  </Section>
</template>

<script lang="ts" setup>
import { computed, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderDateTime, translateEnum } from '@rygen/rygen-ui/i18n';

import { useShipmentStops, useShippingAppointments } from '@/composables';
import { RateQuote, Shipment } from '@/model';

import ShipmentProgressIndicators from '@/components/shipment/tracking/ShipmentProgressIndicators.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  acceptedRateQuote: {
    type: RateQuote,
    default: () => new RateQuote(),
  },
});
const { id, shipment, acceptedRateQuote } = toRefs(props);

const { t } = useI18n();
const { firstUnresolvedStop } = useShipmentStops(shipment, []);

// If we don't have an unresolved stop, then all stops are resolved and the
// shipment is delivered. Use the destination so we show the delivered date
const { primaryAppointment } = useShippingAppointments(
  toRef(() => firstUnresolvedStop.value ?? shipment.value.destination),
);

const mileage = computed(() => {
  let value: number | undefined;
  let label: string | undefined;

  if (acceptedRateQuote.value.totalMileage) {
    value = acceptedRateQuote.value.totalMileage;
    label = t('common.quotedParam', {
      param: t('common.miles', value ?? 2),
    });
  } else if (shipment.value.estimatedMileage) {
    value = shipment.value.estimatedMileage;
    label = t('common.estimatedParam', {
      param: t('common.miles', value ?? 2),
    });
  }

  value = value ? Math.round(value) : value;

  return { label, value };
});

const primaryAppointmentDate = computed(() => {
  if (!primaryAppointment.value.date) {
    return t('common.unknown');
  }

  const date = primaryAppointment.value.date;
  return t('events.dateByTime', {
    date: renderDateTime(date, { timeFormat: 'NONE' }),
    time: renderDateTime(date, { dateFormat: 'NONE' }),
  });
});

const mode = computed(() => {
  return shipment.value.mode
    ? translateEnum(shipment.value.mode, 'Mode')
    : undefined;
});
</script>

<style>
.carrier-portal-highlight-section {
  .section-header {
    flex-flow: column;
    align-items: start;
    padding: var(--sp-6) var(--sp-6) var(--sp-3);
  }

  .shipment-progress-indicators {
    padding: 0 var(--sp-6) var(--sp-6);
  }

  .cell {
    border-right: var(--divider);
    padding: var(--p-card-body-padding);
    flex: 1 0 auto;
    align-items: center;
    width: 20%;

    &:last-child {
      border-right: none;
    }

    .value {
      font-size: 18px;
    }
  }
}
</style>
